import { AxiosError, AxiosResponse } from 'axios';
import appConfig from '../config';
import { getClaimToken, clearClaimToken } from './wizardStorage';

const DRAW_SUBPATH_PATTERN = /\/api\/draws\/([A-Za-z0-9_-]+)(?:\/|\?|$)/;

export function extractDrawIdForClaimSession(url: string | undefined): string | null {
  if (!url) return null;
  const match = url.match(DRAW_SUBPATH_PATTERN);
  return match ? match[1] : null;
}

const PAYMENT_INTENT_URL_PATTERN = /\/api\/payments\/create-payment-intent\/?$/;
const UUID_LIKE_PATTERN = /^[A-Za-z0-9_-]+$/;

export function extractDrawIdFromPaymentRequest(
  url: string | undefined,
  data: unknown
): string | null {
  if (!url || !PAYMENT_INTENT_URL_PATTERN.test(url)) return null;
  let parsed: unknown = data;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  const drawId = (parsed as Record<string, unknown>).draw_id;
  if (typeof drawId !== "string" || !UUID_LIKE_PATTERN.test(drawId)) {
    return null;
  }
  return drawId;
}

/**
 * Utility functions for API interceptors
 * Following clean code principles with separation of concerns
 */

// Type definitions for better type safety
export interface StandardizedError {
  status: number;
  code: string;
  message: string;
  details?: any;
}

export interface ApiErrorData {
  status?: number;
  code?: string;
  message?: string;
  detail?: string;
  details?: any;
}

/**
 * Token Provider type for authentication
 */
export type TokenProvider = () => Promise<string | null>;

/**
 * Notification function type for error display
 */
/**
 * Notification function type for error display - always urgent tone for errors
 */
export type NotificationFunction = (code: string, errorMessage: string, tone?: 'urgent' | 'caution') => void;

/**
 * Refresh token function type for 401 handling
 * Returns the new access token if successful, null otherwise
 */
export type RefreshTokenFunction = () => Promise<string | null>;

/**
 * Login redirect function type for when refresh fails
 */
export type LoginRedirectFunction = () => void;

/**
 * Logout redirect function type for when refresh fails
 */
export type LogoutRedirectFunction = () => void;

export type ClaimSessionExpiredFunction = (drawId: string | null) => void;

/**
 * Authentication Interceptor Utility
 * Handles token injection into requests
 */
export class AuthInterceptor {
  private tokenProvider: TokenProvider | null = null;

  setTokenProvider(provider: TokenProvider): void {
    this.tokenProvider = provider;
  }

  /**
   * Get the current access token
   * This can be used by non-axios code (like fetch) to get auth tokens
   */
  async getAccessToken(): Promise<string | null> {
    if (this.tokenProvider) {
      try {
        return await this.tokenProvider();
      } catch (error) {
        // Token provider failed
        return null;
      }
    }
    return null;
  }

  async handleRequest(config: any): Promise<any> {
    let bearer: string | null = null;
    if (this.tokenProvider) {
      try {
        bearer = await this.tokenProvider();
      } catch (error) {
        // Token provider failed - continue without token
        // The request will get a 401 if authentication is required
      }
    }
    if (bearer) {
      config.headers.Authorization = `Bearer ${bearer}`;
      return config;
    }
    if (appConfig.features.anonymousDraftsEnabled) {
      const drawId =
        extractDrawIdForClaimSession(config.url) ||
        extractDrawIdFromPaymentRequest(config.url, config.data);
      if (drawId) {
        const claimToken = getClaimToken(drawId);
        if (claimToken) {
          config.headers['X-Claim-Token'] = claimToken;
        }
      }
    }
    return config;
  }

  handleRequestError(error: any): Promise<never> {
    return Promise.reject(error);
  }
}

export class ErrorResponseInterceptor {
  private notificationFunction: NotificationFunction | null = null;
  private refreshTokenFunction: RefreshTokenFunction | null = null;
  private logoutRedirectFunction: LogoutRedirectFunction | null = null;
  private claimSessionExpiredFunction: ClaimSessionExpiredFunction | null = null;
  private accessTokenProvider: TokenProvider | null = null;

  setNotificationFunction(fn: NotificationFunction): void {
    this.notificationFunction = fn;
  }

  setAccessTokenProvider(fn: TokenProvider): void {
    this.accessTokenProvider = fn;
  }

  setRefreshTokenFunction(fn: RefreshTokenFunction): void {
    this.refreshTokenFunction = fn;
  }

  setLoginRedirectFunction(fn: LoginRedirectFunction): void {
  }

  setLogoutRedirectFunction(fn: LogoutRedirectFunction): void {
    this.logoutRedirectFunction = fn;
  }

  setClaimSessionExpiredFunction(fn: ClaimSessionExpiredFunction): void {
    this.claimSessionExpiredFunction = fn;
  }

  /**
   * Handles successful responses (pass-through)
   */
  handleResponse(response: AxiosResponse): AxiosResponse {
    return response;
  }

  /**
   * Handles error responses with standardized format extraction
   */
  async handleError(error: AxiosError): Promise<never> {
    const enhancedError = this.extractStandardizedError(error);

    if (
      error.response?.status === 401 &&
      appConfig.features.anonymousDraftsEnabled &&
      this.requestCarriedClaimToken(error)
    ) {
      const drawId = extractDrawIdForClaimSession(error.config?.url);
      if (drawId) {
        clearClaimToken(drawId);
      }

      // Race on login: a draw refetch can fire with the (now-consumed) claim
      // token before the auth provider swaps in the Bearer. If we're actually
      // authenticated now, retry once with the Bearer instead of surfacing a
      // spurious "invalid token" error.
      const bearer = this.accessTokenProvider
        ? await this.accessTokenProvider()
        : null;
      const cfg = error.config as any;
      if (bearer && cfg && !cfg._claimRetry) {
        cfg._claimRetry = true;
        delete cfg.headers["X-Claim-Token"];
        delete cfg.headers["x-claim-token"];
        cfg.headers.Authorization = `Bearer ${bearer}`;
        const axios = (await import("axios")).default;
        return axios.request(cfg);
      }

      if (this.shouldShowNotification(error, enhancedError)) {
        this.showErrorNotification(enhancedError);
      }
      if (this.claimSessionExpiredFunction) {
        this.claimSessionExpiredFunction(drawId);
      }
      return Promise.reject(enhancedError);
    }

    // Handle 401 Unauthorized - try to refresh token first
    if (error.response?.status === 401) {
      if (this.refreshTokenFunction) {
        try {
          const newToken = await this.refreshTokenFunction();

          if (newToken && error.config) {
            // Token refreshed successfully - retry the original request with new token
            const axios = (await import('axios')).default;
            error.config.headers.Authorization = `Bearer ${newToken}`;
            return axios.request(error.config);
          }
        } catch (refreshError) {
          // Refresh failed - continue to show notification and logout
        }
      }

      // Show notification BEFORE logout
      if (this.shouldShowNotification(error, enhancedError)) {
        this.showErrorNotification(enhancedError);
      }

      // Then redirect to logout
      if (this.logoutRedirectFunction) {
        this.logoutRedirectFunction();
      }

      return Promise.reject(enhancedError);
    }

    // Show notification for other HTTP errors (4xx, 5xx)
    if (this.shouldShowNotification(error, enhancedError)) {
      this.showErrorNotification(enhancedError);
    }

    return Promise.reject(enhancedError);
  }

  /**
   * Extracts standardized error information from axios error
   * Backend returns: {"status": 400, "code": "ERROR_CODE", "message": "..."}
   */
  private extractStandardizedError(error: AxiosError): Error & Partial<StandardizedError> {
    let errorMessage = 'An error occurred';
    let status = error.response?.status;
    let code: string | undefined;
    let details: any;

    // Handle API error responses
    if (error.response?.data && typeof error.response.data === 'object') {
      const errorData = error.response.data as any;
      
      // Standardized format: {"status": 400, "code": "ERROR_CODE", "message": "..."}
      if (errorData.status && errorData.code && errorData.message) {
        errorMessage = errorData.message;
        code = errorData.code;
        status = errorData.status;
        details = errorData.details;
      }
      // Legacy fallback: {"detail":{"error":"ERROR_CODE","message":"..."}}
      else if (errorData.detail && typeof errorData.detail === 'object') {
        errorMessage = errorData.detail.message || 'An error occurred';
        code = errorData.detail.error;
        details = errorData.detail;
      }
      // Fallback: {"detail": "string message"}
      else if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      }
    }
    // Network error - no response
    else if (error.request) {
      errorMessage = 'Network error occurred';
      code = 'NETWORK_ERROR';
      status = 0;
    }
    // Other errors
    else {
      errorMessage = error.message || 'An unexpected error occurred';
      code = 'UNKNOWN_ERROR';
    }

    // Create error object
    const apiError = new Error(errorMessage) as Error & Partial<StandardizedError>;
    if (status !== undefined) apiError.status = status;
    if (code) apiError.code = code;
    if (details) apiError.details = details;
    
    return apiError;
  }

  private requestCarriedClaimToken(error: AxiosError): boolean {
    const headers = (error.config?.headers as Record<string, unknown>) || {};
    return Boolean(headers['X-Claim-Token'] || headers['x-claim-token']);
  }

  /**
   * Determines if notification should be shown for this error
   */
  private shouldShowNotification(
    error: AxiosError,
    enhancedError: Error & Partial<StandardizedError>
  ): boolean {
    if (
      !this.notificationFunction ||
      !error.response ||
      error.response.status < 400
    ) {
      return false;
    }
    const skipFn = (error.config as any)?.skipNotificationFor;
    if (typeof skipFn === 'function') {
      try {
        if (skipFn(enhancedError)) return false;
      } catch {
      }
    }
    return true;
  }

  /**
   * Shows error notification with enhanced details - always urgent tone for API errors
   */
  private showErrorNotification(enhancedError: Error & Partial<StandardizedError>): void {
    if (this.notificationFunction) {
      const errorMessage = enhancedError.message || 'An error occurred';
      this.notificationFunction('ERROR', errorMessage, 'urgent');
    }
  }
}

/**
 * Factory for creating interceptor instances
 * Provides a clean interface for setting up interceptors
 */
export class InterceptorFactory {
  static createAuthInterceptor(): AuthInterceptor {
    return new AuthInterceptor();
  }

  static createErrorInterceptor(): ErrorResponseInterceptor {
    return new ErrorResponseInterceptor();
  }
}